// RichText ↔ contenteditable DOM. The parser accepts the markup browsers
// produce while editing (div/p/br paragraphs, b/strong, i/em, a, ul/li, and
// styled spans) and ignores anything else.
import type { RichText, RichTextBlock, RichTextRun } from '../../shared/types.ts';
import { normalizeRichText, sanitizeHref } from '../../shared/rich-text.ts';

/** The subset of DOM Node the parser reads; real DOM nodes satisfy it. */
export interface DomNode {
  nodeType: number;
  nodeName: string;
  textContent: string | null;
  childNodes: ArrayLike<DomNode>;
  getAttribute?(name: string): string | null;
  style?: { fontWeight?: string; fontStyle?: string };
}

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;
const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE']);
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE']);

type Marks = Omit<RichTextRun, 't'>;

export function richTextFromDom(root: DomNode): RichText {
  const blocks: RichTextBlock[] = [];
  let cur: RichTextBlock | null = null;

  const open = (list: boolean): RichTextBlock => {
    cur = list ? { list: 'bullet', runs: [] } : { runs: [] };
    blocks.push(cur);
    return cur;
  };

  const addText = (text: string, marks: Marks, list: boolean) => {
    const parts = text.replace(/ /g, ' ').replace(/[​﻿]/g, '').split(/\r\n|\r|\n/);
    parts.forEach((part, i) => {
      if (i > 0) cur = null; // a literal newline starts a new paragraph
      if (part) (cur ?? open(list)).runs.push({ t: part, ...marks });
    });
  };

  const walk = (node: DomNode, marks: Marks, list: boolean) => {
    if (node.nodeType === TEXT_NODE) return addText(node.textContent ?? '', marks, list);
    if (node.nodeType !== ELEMENT_NODE) return;
    const tag = node.nodeName.toUpperCase();
    if (SKIP_TAGS.has(tag)) return;

    if (tag === 'BR') {
      // A <br> ends the current line; on an empty line it *is* the (blank) line.
      if (cur) cur = null;
      else { open(list); cur = null; }
      return;
    }

    const m: Marks = { ...marks };
    if (tag === 'B' || tag === 'STRONG') m.b = 1;
    if (tag === 'I' || tag === 'EM') m.i = 1;
    const weight = node.style?.fontWeight;
    if (weight === 'bold' || Number(weight) >= 600) m.b = 1;
    else if (weight === 'normal' || (Number(weight) > 0 && Number(weight) < 600)) delete m.b;
    const fontStyle = node.style?.fontStyle;
    if (fontStyle === 'italic' || fontStyle === 'oblique') m.i = 1;
    else if (fontStyle === 'normal') delete m.i;
    if (tag === 'A') {
      const href = sanitizeHref(node.getAttribute?.('href'));
      if (href) m.href = href;
    }

    const inList = list || tag === 'LI';
    const children = Array.from(node.childNodes);
    if (BLOCK_TAGS.has(tag)) {
      cur = null;
      const before = blocks.length;
      for (const child of children) walk(child, m, inList);
      if (blocks.length === before && tag !== 'UL' && tag !== 'OL') open(inList); // empty paragraph
      cur = null;
      return;
    }
    for (const child of children) walk(child, m, inList);
  };

  for (const child of Array.from(root.childNodes)) walk(child, {}, false);
  return normalizeRichText({ blocks });
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escapeAttr = (s: string) => escapeHtml(s).replace(/"/g, '&quot;');

function runHtml(r: RichTextRun): string {
  let s = escapeHtml(r.t);
  if (r.i) s = `<i>${s}</i>`;
  if (r.b) s = `<b>${s}</b>`;
  const href = sanitizeHref(r.href);
  if (href) s = `<a href="${escapeAttr(href)}">${s}</a>`;
  return s;
}

export function richTextToHtml(rt: RichText | undefined): string {
  const blocks = rt?.blocks.length ? rt.blocks : [{ runs: [] }];
  let html = '';
  let inList = false;
  for (const b of blocks) {
    if (b.list && !inList) { html += '<ul>'; inList = true; }
    if (!b.list && inList) { html += '</ul>'; inList = false; }
    const inner = b.runs.map(runHtml).join('') || '<br>';
    html += b.list ? `<li>${inner}</li>` : `<div>${inner}</div>`;
  }
  if (inList) html += '</ul>';
  return html;
}

export function linkHtml(href: string, text: string): string {
  return `<a href="${escapeAttr(href)}">${escapeHtml(text)}</a>`;
}
